import React from "react";
import {observable} from "mobx";
import SmartviewManagerContext from "../contexts/SmartviewManagerContext";
import uniqueId from 'lodash/uniqueId';

export default class SmartviewManager extends React.Component {

    currentRoot = null;
    smartview = null;
    smartviews = [];

    static get defaultProps() {
        return {
            defaultSize: 'xs',
        };
    }


    @observable config = new Map([['size', 'xs']]);

    constructor(props) {
        super(props);

        this.mount.bind(this);
        this.reset.bind(this);

        this.resize();

    }

    unmount() {
        return new Promise((resolve, reject) => {

            this.smartview = null;
            this.triggerOnMountCallbacks();

            setTimeout(() => {
                resolve();
            }, 0);
        });
    }

    reset() {
        return new Promise((resolve, reject) => {

            this.config.set('size', this.props.defaultSize);
            this.smartviews = [];
            this.currentRoot = null;
            this.smartview = null;
            this.triggerOnMountCallbacks();

            setTimeout(() => {
                this.activate()
                    .then(() => {
                        resolve();
                    })
                    .catch(err => {
                        reject(err);
                    });
            }, 0);
        });
    }

    /**
     * Clean tree after the current root
     * @return Promise
     */
    cleanTree(base = []) {
        return new Promise(resolve => {
            this.smartviews = (base && base.length > 0) ? base : [this.currentRoot];
            this.currentRoot = this.getLastSmartview();
            resolve();
        });
    }

    /**
     * Adds a new root and cleans the remaining tree
     * @param component
     * @param props
     */
    mount(component, props = {}) {
        this.cleanTree([{component, props}])
            .then(() => {
                return this.activate();
            });
    }

    /**
     * Resets the tree and pushes the component on top
     * @param component
     * @param props
     */
    push(component, props = {}, keepTree = false) {
        this.cleanTree(keepTree ? this.smartviews : [])
            .then(() => {
                this.smartviews.push({component, props});
                return this.activate();
            });
    }

    /**
     *
     * @returns {null|React.DetailedReactHTMLElement<*, HTMLElement>}
     */
    buildSmartview() {
        let smartview = this.getLastSmartview();

        if (smartview && smartview.component && smartview.props) {
            return React.cloneElement(
                smartview.component,
                {
                    key: uniqueId(),
                    ...smartview.props
                }
            );
        }

        return null;
    }

    /**
     *
     * @returns {Promise<unknown>}
     */
    activate() {
        return Promise.resolve(null)
            .then(() => this.triggerOnBeforeMountCallbacks())
            .then(() => {
                this.smartview = this.buildSmartview();
                if (!this.smartview) {
                    this.resize();
                }
                this.triggerOnMountCallbacks();
            });

    }

    /**
     * Helper to tell if
     * @returns {boolean}
     */
    hasSmartviews = () => {
        return this.smartviews.length > 0;
    }

    /**
     * Trigger a resize of the smartview
     * @param size
     */
    resize = (size = 'xs') => {

        if(this.config.get('size') === size) {
            return;
        }

        this.config.set('size', size);
    }

    /**
     * Return the last smartview item of the smartviews array
     * @returns {*}
     */
    getLastSmartview() {
        if(!this.hasSmartviews()) {
            return null;
        }
        return this.smartviews[this.smartviews.length - 1];
    }

    /////////////////////////////////
    // Back helpers
    /////////////////////////////////

    canGoBack = () => {
        return this.smartviews.length > 1;
    }

    back() {

        if (!this.canGoBack()) {
            return;
        }

        const smartviewsAfterBack = [...this.smartviews]
        smartviewsAfterBack.pop();

        this.cleanTree(smartviewsAfterBack).then(() => {
            return this.activate();
        })

    }

    /////////////////////////////////
    // Events helpers
    /////////////////////////////////

    onBeforeMountCallbacks = [];

    onBeforeMount(callback) {
        this.onBeforeMountCallbacks.push(callback);

        const disposer = () => {
            this.removeOnBeforeMountCallback(callback);
        }

        return disposer;
    }

    removeOnBeforeMountCallback(callback) {
        if (this.onBeforeMountCallbacks.length > 0) {
            this.onBeforeMountCallbacks.forEach((onBeforeMountCallback, index) => {
                if (this.onBeforeMountCallbacks[index] === callback) {
                    delete this.onBeforeMountCallbacks[index];
                }
            })
        }
    }

    triggerOnBeforeMountCallbacks() {
        return Promise.resolve()
            .then(() => {
                if (this.onBeforeMountCallbacks.length > 0) {
                    return Promise.all(this.onBeforeMountCallbacks.map(callback => callback()))
                }
            })
    }

    onMountCallbacks = [];

    onMount(callback) {
        this.onMountCallbacks.push(callback);

        const disposer = () => {
            this.removeOnMountCallback(callback);
        }

        return disposer;
    }

    removeOnMountCallback(callback) {
        if (this.onMountCallbacks.length > 0) {
            this.onMountCallbacks.forEach((onMountCallback, index) => {
                if (this.onMountCallbacks[index] === callback) {
                    delete this.onMountCallbacks[index];
                }
            })
        }
    }

    triggerOnMountCallbacks() {
        if (this.onMountCallbacks.length > 0) {
            this.onMountCallbacks.forEach((callback, index) => {
                callback();
            })
        }
    }

    onClickOutsideCallbacks = [];

    onClickOutside(callback) {
        this.onClickOutsideCallbacks.push(callback);

        const disposer = () => {
            this.removeOnClickOutsideCallback(callback);
        }

        return disposer;
    }

    removeOnClickOutsideCallback(callback) {
        if (this.onClickOutsideCallbacks.length > 0) {
            this.onClickOutsideCallbacks.forEach((onClickOutsideCallback, index) => {
                if (this.onClickOutsideCallbacks[index] === callback) {
                    delete this.onClickOutsideCallbacks[index];
                }
            })
        }
    }

    handleClickOutside = () => {
        if (this.onClickOutsideCallbacks.length > 0) {
            this.onClickOutsideCallbacks.forEach((onClickOutsideCallback, index) => {
                onClickOutsideCallback();
            })
        }
    }

    /////////////////////////////////
    // Rendering
    /////////////////////////////////

    render() {
        return (
            <SmartviewManagerContext.Provider value={this}>
                {this.props.children}
            </SmartviewManagerContext.Provider>
        );
    }

}
